import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEllipsisV } from "react-icons/fa";

const SERVICE_OPTIONS = ["Initial Assessment", "Therapy"];
const INSURANCE_STATUSES = ["Active", "Pending", "Inactive"];
const SELF_PAY_SUBTITLE =
  "Patient pays directly — no insurance required.";
const ACCESS_CODE_SUBTITLE =
  "Patients must use a valid access code to book sessions at reduced or no cost.";

const STATE_ABBR = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

const toStateCode = (state) => {
  if (!state) return "—";
  if (STATE_ABBR[state]) return STATE_ABBR[state];
  if (String(state).length <= 3) return String(state).toUpperCase();
  return String(state);
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(String(value).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(num)) return "—";
  return `$${num.toFixed(2)}`;
};

const formatDisplayDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const [y, m, d] = raw.split("-");
  if (y && m && d) return `${m}/${d}/${y}`;
  return value;
};

const emptyPayer = () => ({
  state: "",
  payer: "",
  service: "",
  contractedRate: "",
  effectiveDate: "",
  endDate: "",
  status: "Active",
});

const normalizePayers = (insurance) => {
  if (!insurance) return [];

  let items = insurance;
  if (typeof items === "string") {
    if (items.includes("[object Object]")) return [];
    try {
      items = JSON.parse(items);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => {
      if (!item) return false;
      if (typeof item === "string") return !item.includes("[object Object]");
      return typeof item === "object" && !Array.isArray(item);
    })
    .map((item) => {
      if (typeof item === "string") {
        try {
          item = JSON.parse(item);
        } catch {
          return null;
        }
      }
      if (!item || typeof item !== "object") return null;
      return {
        state: item.state || "",
        payer: item.payer || item.payerName || item.insurance_name || "",
        service: item.service || "",
        contractedRate:
          item.contractedRate !== undefined && item.contractedRate !== null
            ? String(item.contractedRate)
            : item.rate !== undefined && item.rate !== null
              ? String(item.rate)
              : "",
        effectiveDate: item.effectiveDate
          ? String(item.effectiveDate).slice(0, 10)
          : item.effectivedate
            ? String(item.effectivedate).slice(0, 10)
            : "",
        endDate: item.endDate
          ? String(item.endDate).slice(0, 10)
          : item.enddate
            ? String(item.enddate).slice(0, 10)
            : "",
        status: item.status || "Active",
      };
    })
    .filter(Boolean);
};

const formatStateOptionLabel = (stateName) => {
  if (!stateName) return "";
  const code = toStateCode(stateName);
  return code && code !== "—" ? `${stateName} (${code})` : stateName;
};

const CODE_TO_STATE = Object.fromEntries(
  Object.entries(STATE_ABBR).map(([name, code]) => [code.toLowerCase(), name]),
);

/** Prefer full state name when credentials store an abbreviation (e.g. MD → Maryland). */
const resolveStateName = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (STATE_ABBR[value]) return value;
  const fromCode = CODE_TO_STATE[value.toLowerCase()];
  return fromCode || value;
};

/**
 * Unique licensed state names from provider credentials.
 * Only Active / Pending Review licenses count (Inactive & Expired are excluded).
 */
const getLicensedStateNames = (credentials) => {
  if (!credentials) return [];
  let list = credentials;
  if (typeof credentials === "string") {
    if (credentials.includes("[object Object]")) return [];
    try {
      list = JSON.parse(credentials);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];

  const names = [];
  const seen = new Set();
  list.forEach((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return;
    const status = String(item.status || "Active").trim().toLowerCase();
    if (status === "inactive" || status === "expired") return;

    const state = resolveStateName(item.state);
    if (!state) return;
    const key = state.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(state);
  });
  return names;
};

const getPayerLabel = (provider) =>
  provider?.insurance_name || provider?.insuranceName || provider?.name || "";

const InsuranceStatusBadge = ({ status }) => {
  const tone =
    status === "Active"
      ? "active"
      : status === "Pending"
        ? "pending"
        : "inactive";
  return (
    <span className={`pp-insurance-status pp-insurance-status-${tone}`}>
      {status}
    </span>
  );
};

const ProviderPaymentsTab = ({
  provider,
  getProviderId,
  onProviderUpdated,
  fetchedStates = [],
  readOnlyInsurance = false,
  hideAccessCode = false,
}) => {
  const [editingSelfPay, setEditingSelfPay] = useState(false);
  const [selfPayDraft, setSelfPayDraft] = useState("");
  const [selfPayError, setSelfPayError] = useState("");
  const [savingSelfPay, setSavingSelfPay] = useState(false);
  const [savingAccessCode, setSavingAccessCode] = useState(false);

  const [payers, setPayers] = useState(() => normalizePayers(provider?.insurance));
  const [stateFilter, setStateFilter] = useState("All States");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [savingPayer, setSavingPayer] = useState(false);
  const [payerDrawer, setPayerDrawer] = useState(null);
  const [payerErrors, setPayerErrors] = useState({});
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [statePayerOptions, setStatePayerOptions] = useState([]);
  const [loadingPayerOptions, setLoadingPayerOptions] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!editingSelfPay) {
      setSelfPayDraft(provider?.fees ? String(provider.fees) : "");
    }
  }, [provider?.fees, editingSelfPay]);

  useEffect(() => {
    setPayers(normalizePayers(provider?.insurance));
  }, [provider?.insurance]);

  useEffect(() => {
    const closeMenu = () => {
      setMenuOpenIndex(null);
      setMenuPosition(null);
    };

    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        closeMenu();
      }
    };

    const onScrollOrResize = () => {
      if (menuOpenIndex !== null) closeMenu();
    };

    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [menuOpenIndex]);

  useEffect(() => {
    const fetchInsuranceProviders = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/insuranceProvider/all`,
        );
        if (response.data?.success) {
          setInsuranceProviders(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching insurance providers:", error);
      }
    };

    fetchInsuranceProviders();
  }, []);

  useEffect(() => {
    if (!payerDrawer?.draft.state) {
      setStatePayerOptions([]);
      return;
    }

    const stateRecord = fetchedStates.find(
      (state) => state.name === payerDrawer.draft.state,
    );

    const loadStatePayers = async () => {
      setLoadingPayerOptions(true);
      try {
        if (stateRecord?._id) {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/states/${stateRecord._id}/insurances`,
          );
          if (response.data?.success && Array.isArray(response.data.data)) {
            setStatePayerOptions(response.data.data);
            return;
          }
        }

        setStatePayerOptions(
          insuranceProviders.filter(
            (item) => String(item.status || "active").toLowerCase() !== "inactive",
          ),
        );
      } catch (error) {
        console.error("Error fetching state insurance payers:", error);
        setStatePayerOptions(
          insuranceProviders.filter(
            (item) => String(item.status || "active").toLowerCase() !== "inactive",
          ),
        );
      } finally {
        setLoadingPayerOptions(false);
      }
    };

    loadStatePayers();
  }, [payerDrawer?.draft.state, fetchedStates, insuranceProviders]);

  const payerDropdownOptions = useMemo(() => {
    const names = statePayerOptions
      .map((item) => getPayerLabel(item))
      .filter(Boolean);
    const current = payerDrawer?.draft?.payer?.trim();
    const unique = [...new Set(names)];
    if (current && !unique.some((name) => name.toLowerCase() === current.toLowerCase())) {
      unique.unshift(current);
    }
    return unique.sort((a, b) => a.localeCompare(b));
  }, [statePayerOptions, payerDrawer?.draft?.payer]);

  const licensedStateOptions = useMemo(() => {
    const licensedNames = getLicensedStateNames(provider?.credentials);
    if (!licensedNames.length) return [];

    const licensedKeys = new Set(
      licensedNames.flatMap((name) => {
        const keys = [name.toLowerCase()];
        const code = toStateCode(name);
        if (code && code !== "—") keys.push(code.toLowerCase());
        return keys;
      }),
    );

    const matchesLicense = (stateName) => {
      if (!stateName) return false;
      const lower = stateName.toLowerCase();
      if (licensedKeys.has(lower)) return true;
      const code = toStateCode(stateName);
      return Boolean(
        code && code !== "—" && licensedKeys.has(code.toLowerCase()),
      );
    };

    const fromFetched = (fetchedStates || []).filter((opt) =>
      matchesLicense(opt.name),
    );

    const ensureCurrent = (options) => {
      const current = payerDrawer?.draft?.state;
      if (
        current &&
        !options.some(
          (opt) =>
            String(opt.name || "").toLowerCase() === current.toLowerCase(),
        )
      ) {
        return [...options, { name: current, _id: `current-${current}` }];
      }
      return options;
    };

    if (fromFetched.length) return ensureCurrent(fromFetched);

    return ensureCurrent(
      licensedNames.map((name) => ({ name, _id: name })),
    );
  }, [provider?.credentials, fetchedStates, payerDrawer?.draft?.state]);

  const persistProvider = async (payload) => {
    const request = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
      {
        email: provider.email,
        firstname: provider.firstname,
        lastname: provider.lastname,
        providerType: provider.providerType,
        ...payload,
      },
    );
    return request;
  };

  const selfPayRate = provider?.fees ? formatMoney(provider.fees) : null;
  const accessCodeEnabled = Boolean(provider?.accessCodeEnabled);

  const filteredPayers = useMemo(() => {
    return payers
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        const stateMatch =
          stateFilter === "All States" ||
          item.state === stateFilter ||
          toStateCode(item.state) === stateFilter;
        const statusMatch =
          statusFilter === "All Statuses" || item.status === statusFilter;
        return stateMatch && statusMatch;
      });
  }, [payers, stateFilter, statusFilter]);

  const stateFilterOptions = useMemo(() => {
    const states = [...new Set(payers.map((p) => p.state).filter(Boolean))];
    return ["All States", ...states];
  }, [payers]);

  const startEditSelfPay = () => {
    setSelfPayDraft(provider?.fees ? String(provider.fees) : "");
    setSelfPayError("");
    setEditingSelfPay(true);
  };

  const cancelEditSelfPay = () => {
    setSelfPayDraft(provider?.fees ? String(provider.fees) : "");
    setSelfPayError("");
    setEditingSelfPay(false);
  };

  const saveSelfPay = async () => {
    const raw = String(selfPayDraft || "").trim();
    if (!raw) {
      setSelfPayError("Per session rate is required.");
      return;
    }
    const feeValue = raw.replace(/[^0-9.]/g, "");
    if (!feeValue || Number.isNaN(Number(feeValue)) || Number(feeValue) < 0) {
      setSelfPayError("Enter a valid amount.");
      return;
    }

    try {
      setSavingSelfPay(true);
      const request = await persistProvider({ fees: feeValue });
      if (request.data.success) {
        onProviderUpdated({
          ...provider,
          fees: feeValue,
        });
        setEditingSelfPay(false);
        toast.success("Self-pay rate updated.");
      } else {
        toast.error(request.data.message || "Failed to update self-pay rate.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update self-pay rate.",
      );
    } finally {
      setSavingSelfPay(false);
    }
  };

  const toggleAccessCode = async () => {
    const next = !accessCodeEnabled;
    try {
      setSavingAccessCode(true);
      const request = await persistProvider({ accessCodeEnabled: next });
      if (request.data.success) {
        onProviderUpdated({
          ...provider,
          accessCodeEnabled: next,
        });
        toast.success(
          next ? "Access code payments enabled." : "Access code payments disabled.",
        );
      } else {
        toast.error(request.data.message || "Failed to update access code.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update access code.",
      );
    } finally {
      setSavingAccessCode(false);
    }
  };

  const savePayers = async (nextPayers, successMessage) => {
    try {
      setSavingPayer(true);
      const request = await persistProvider({ insurance: nextPayers });
      if (request.data.success) {
        setPayers(nextPayers);
        onProviderUpdated({
          ...provider,
          insurance: nextPayers,
        });
        toast.success(successMessage);
        return true;
      }
      toast.error(request.data.message || "Failed to update insurance payers.");
      return false;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update insurance payers.",
      );
      return false;
    } finally {
      setSavingPayer(false);
    }
  };

  const openAddPayer = () => {
    setPayerErrors({});
    setPayerDrawer({ mode: "add", index: -1, draft: emptyPayer() });
    setMenuOpenIndex(null);
    setMenuPosition(null);
  };

  const openEditPayer = (index) => {
    setPayerErrors({});
    setPayerDrawer({ mode: "edit", index, draft: { ...payers[index] } });
    setMenuOpenIndex(null);
    setMenuPosition(null);
  };

  const closePayerDrawer = () => {
    setPayerDrawer(null);
    setPayerErrors({});
  };

  const updatePayerDraft = (field, value) => {
    if (field === "effectiveDate" || field === "endDate") {
      if (value) {
        const year = value.split("-")[0];
        if (year.length > 4) return;
      }
    }
    setPayerDrawer((prev) => {
      if (!prev) return prev;
      const draft = { ...prev.draft, [field]: value };
      if (field === "state" && prev.mode === "add") {
        draft.payer = "";
      }
      return { ...prev, draft };
    });
  };

  const validatePayerDraft = (draft, mode, editIndex) => {
    const errors = {};
    if (!draft.state) errors.state = "State is required.";
    if (!draft.payer?.trim()) errors.payer = "Payer is required.";
    if (!draft.service) errors.service = "Service is required.";
    if (!draft.contractedRate?.trim()) {
      errors.contractedRate = "Contracted rate is required.";
    } else {
      const rate = Number(String(draft.contractedRate).replace(/[^0-9.]/g, ""));
      if (Number.isNaN(rate) || rate < 0) {
        errors.contractedRate = "Enter a valid contracted rate.";
      }
    }
    if (!draft.effectiveDate) {
      errors.effectiveDate = "Effective date is required.";
    }

    const nextStatus =
      mode === "add" ? "Active" : payers[editIndex]?.status || "Active";

    if (draft.state && draft.payer?.trim() && draft.service) {
      const duplicate = payers.some((item, index) => {
        if (mode === "edit" && index === editIndex) return false;
        return (
          item.state === draft.state &&
          item.payer.trim().toLowerCase() === draft.payer.trim().toLowerCase() &&
          item.service === draft.service &&
          item.status === "Active" &&
          nextStatus === "Active"
        );
      });
      if (duplicate) {
        errors.payer =
          "An active payer already exists for this state, payer, and service.";
      }
    }

    return errors;
  };

  const submitPayerDrawer = async () => {
    if (!payerDrawer) return;
    const errors = validatePayerDraft(
      payerDrawer.draft,
      payerDrawer.mode,
      payerDrawer.index,
    );
    setPayerErrors(errors);
    if (Object.keys(errors).length) {
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }

    const draft = {
      ...payerDrawer.draft,
      payer: payerDrawer.draft.payer.trim(),
      contractedRate: String(payerDrawer.draft.contractedRate).replace(
        /[^0-9.]/g,
        "",
      ),
      endDate: payerDrawer.draft.endDate || "",
      status:
        payerDrawer.mode === "add"
          ? "Active"
          : payers[payerDrawer.index]?.status || "Active",
    };

    let next;
    if (payerDrawer.mode === "add") {
      next = [...payers, draft];
    } else {
      next = payers.map((item, i) =>
        i === payerDrawer.index ? draft : item,
      );
    }

    const ok = await savePayers(
      next,
      payerDrawer.mode === "add"
        ? "Insurance payer added."
        : "Insurance payer updated.",
    );
    if (ok) closePayerDrawer();
  };

  const openDeactivateConfirm = (index) => {
    setMenuOpenIndex(null);
    setMenuPosition(null);
    setDeactivateTarget(index);
  };

  const closeDeactivateConfirm = () => {
    if (!savingPayer) setDeactivateTarget(null);
  };

  const confirmDeactivatePayer = async () => {
    if (deactivateTarget === null) return;
    const next = payers.map((item, index) =>
      index === deactivateTarget ? { ...item, status: "Inactive" } : item,
    );
    const ok = await savePayers(next, "Insurance payer deactivated.");
    if (ok) setDeactivateTarget(null);
  };

  const activatePayer = async (index) => {
    setMenuOpenIndex(null);
    setMenuPosition(null);
    const target = payers[index];
    if (!target) return;

    const duplicate = payers.some(
      (item, i) =>
        i !== index &&
        item.status === "Active" &&
        item.state === target.state &&
        item.payer.trim().toLowerCase() === target.payer.trim().toLowerCase() &&
        item.service === target.service,
    );
    if (duplicate) {
      toast.error(
        "An active payer already exists for this state, payer, and service.",
      );
      return;
    }

    const next = payers.map((item, i) =>
      i === index ? { ...item, status: "Active" } : item,
    );
    await savePayers(next, "Insurance payer activated.");
  };

  const togglePayerMenu = (sourceIndex, event) => {
    if (menuOpenIndex === sourceIndex) {
      setMenuOpenIndex(null);
      setMenuPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right,
    });
    setMenuOpenIndex(sourceIndex);
  };

  const activeMenuPayer =
    menuOpenIndex !== null ? payers[menuOpenIndex] : null;

  return (
    <div className="pp-payments-tab">
      {/* Self Pay */}
      <div className="pp-section">
        <div className="pp-section-header">
          <div>
            <h3>{hideAccessCode ? "Self-Pay" : "Self Pay"}</h3>
            <p className="pp-section-header-subtitle">{SELF_PAY_SUBTITLE}</p>
          </div>
          {!editingSelfPay && (
            <div className="pp-section-header-actions">
              <button
                type="button"
                className="pp-section-edit pp-credentials-header-btn"
                onClick={startEditSelfPay}
              >
                Edit
              </button>
            </div>
          )}
        </div>
        <div className="pp-section-body">
          {editingSelfPay ? (
            <>
              <div className="pp-form-field pp-self-pay-field">
                <label className="pp-info-label">Per session rate</label>
                <div className="pp-money-input-wrap">
                  <span className="pp-money-prefix">$</span>
                  <input
                    className={`pp-form-input pp-money-input ${
                      selfPayError ? "pp-form-input-error" : ""
                    }`}
                    value={selfPayDraft}
                    onChange={(e) => {
                      setSelfPayDraft(e.target.value.replace(/[^0-9.]/g, ""));
                      setSelfPayError("");
                    }}
                    placeholder="150.00"
                    inputMode="decimal"
                  />
                </div>
                {selfPayError && (
                  <span className="pp-field-error">{selfPayError}</span>
                )}
              </div>
              <div className="pp-card-footer pp-self-pay-footer">
                <span className="pp-unsaved-label">Unsaved changes</span>
                <div className="pp-card-footer-actions pp-npi-footer-actions">
                  <button
                    type="button"
                    className="pp-btn-cancel"
                    onClick={cancelEditSelfPay}
                    disabled={savingSelfPay}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pp-btn-save"
                    onClick={saveSelfPay}
                    disabled={savingSelfPay}
                  >
                    {savingSelfPay ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="pp-self-pay-display">
              <div className="pp-info-label">Per session rate</div>
              <div className="pp-self-pay-rate">
                {selfPayRate || "Not set yet"}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Access Code */}
      {!hideAccessCode && (
        <div className="pp-section">
          <div className="pp-section-header">
            <div>
              <h3>Access Code</h3>
              <p className="pp-section-header-subtitle">{ACCESS_CODE_SUBTITLE}</p>
            </div>
            <div className="pp-section-header-actions">
              <button
                type="button"
                className={`pp-toggle ${accessCodeEnabled ? "on" : ""}`}
                role="switch"
                aria-checked={accessCodeEnabled}
                aria-label="Toggle access code payments"
                onClick={toggleAccessCode}
                disabled={savingAccessCode}
              >
                <span className="pp-toggle-thumb" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insurance */}
      <div className="pp-section pp-insurance-section">
        <div className="pp-insurance-divider">
          <span>Insurance</span>
        </div>
        <div className="pp-insurance-toolbar">
          <div className="pp-insurance-filters">
            <select
              className="pp-form-input pp-form-select pp-insurance-filter"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              aria-label="Filter insurance payers by state"
            >
              {stateFilterOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <select
              className="pp-form-input pp-form-select pp-insurance-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter insurance payers by status"
            >
              <option value="All Statuses">All Statuses</option>
              {INSURANCE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <span className="pp-insurance-count">
              {filteredPayers.length} record
              {filteredPayers.length === 1 ? "" : "s"}
            </span>
          </div>
          {!readOnlyInsurance && (
            <button
              type="button"
              className="pp-add-payer-btn"
              onClick={openAddPayer}
              disabled={savingPayer}
            >
              + Add Insurance Payer
            </button>
          )}
        </div>
        <div className="pp-section-body pp-insurance-table-body">
          <div className="pp-insurance-table-wrap">
            <table className="pp-insurance-table">
              <thead>
                <tr>
                  <th>State</th>
                  <th>Payer</th>
                  <th>Service</th>
                  <th>Contracted Rate</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  {!readOnlyInsurance && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {!filteredPayers.length ? (
                  <tr>
                    <td
                      colSpan={readOnlyInsurance ? 7 : 8}
                      className="pp-insurance-empty-cell"
                    >
                      {payers.length
                        ? "No insurance payers match the selected filters."
                        : "No insurance payer added yet"}
                    </td>
                  </tr>
                ) : (
                  filteredPayers.map(({ item: payer, index: sourceIndex }) => (
                    <tr key={`${payer.state}-${payer.payer}-${sourceIndex}`}>
                      <td>
                        <span className="pp-state-badge">
                          {toStateCode(payer.state)}
                        </span>
                      </td>
                      <td>{payer.payer || "—"}</td>
                      <td>{payer.service || "—"}</td>
                      <td>{formatMoney(payer.contractedRate)}</td>
                      <td>{formatDisplayDate(payer.effectiveDate)}</td>
                      <td>{formatDisplayDate(payer.endDate) || "—"}</td>
                      <td>
                        <InsuranceStatusBadge status={payer.status} />
                      </td>
                      {!readOnlyInsurance && (
                        <td className="pp-license-actions-cell">
                          <div className="pp-license-menu-wrap">
                            <button
                              type="button"
                              className="pp-license-menu-btn"
                              aria-label="Payer actions"
                              aria-expanded={menuOpenIndex === sourceIndex}
                              onClick={(event) =>
                                togglePayerMenu(sourceIndex, event)
                              }
                            >
                              <FaEllipsisV size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {!readOnlyInsurance &&
        menuOpenIndex !== null &&
        menuPosition &&
        activeMenuPayer && (
        <div
          ref={menuRef}
          className="pp-license-menu pp-insurance-menu-fixed"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          <button
            type="button"
            onClick={() => openEditPayer(menuOpenIndex)}
          >
            Edit Insurance Payer
          </button>
          {activeMenuPayer.status === "Inactive" ? (
            <button
              type="button"
              onClick={() => activatePayer(menuOpenIndex)}
              disabled={savingPayer}
              style={{ color: "#047857" }}
            >
              Activate Insurance Payer
            </button>
          ) : (
            <button
              type="button"
              className="pp-license-menu-danger"
              onClick={() => openDeactivateConfirm(menuOpenIndex)}
              disabled={savingPayer}
            >
              Deactivate Insurance Payer
            </button>
          )}
        </div>
      )}

      {!readOnlyInsurance && payerDrawer && (
        <div
          className="pp-license-drawer-overlay"
          onClick={closePayerDrawer}
        >
          <aside
            className="pp-license-drawer"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pp-license-drawer-header">
              <h2>
                {payerDrawer.mode === "add"
                  ? "Add Insurance Payer"
                  : "Edit Insurance Payer"}
              </h2>
              <p>
                {payerDrawer.mode === "add"
                  ? "Enter the insurance payer details below."
                  : "Update the insurance payer details below."}
              </p>
            </div>
            <div className="pp-license-drawer-body">
              <div className="pp-form-field">
                <label className="pp-info-label">
                  State <span className="pp-required">*</span>
                </label>
                <select
                  className={`pp-form-input pp-form-select ${
                    payerErrors.state ? "pp-form-input-error" : ""
                  }`}
                  value={payerDrawer.draft.state}
                  onChange={(e) => updatePayerDraft("state", e.target.value)}
                >
                  <option value="">
                    {licensedStateOptions.length
                      ? "Select state"
                      : "No licensed states"}
                  </option>
                  {licensedStateOptions.map((opt) => (
                    <option key={opt._id || opt.name} value={opt.name}>
                      {formatStateOptionLabel(opt.name)}
                    </option>
                  ))}
                </select>
                {!licensedStateOptions.length && (
                  <span className="pp-field-error">
                    Add a state license first to select insurance states.
                  </span>
                )}
                {payerErrors.state && (
                  <span className="pp-field-error">{payerErrors.state}</span>
                )}
              </div>
              <div className="pp-form-field">
                <label className="pp-info-label">
                  Payer <span className="pp-required">*</span>
                </label>
                <select
                  className={`pp-form-input pp-form-select ${
                    payerErrors.payer ? "pp-form-input-error" : ""
                  }`}
                  value={payerDrawer.draft.payer}
                  onChange={(e) => updatePayerDraft("payer", e.target.value)}
                  disabled={!payerDrawer.draft.state || loadingPayerOptions}
                >
                  <option value="">
                    {!payerDrawer.draft.state
                      ? "Select state first"
                      : loadingPayerOptions
                        ? "Loading payers..."
                        : "Select payer"}
                  </option>
                  {payerDropdownOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {payerErrors.payer && (
                  <span className="pp-field-error">{payerErrors.payer}</span>
                )}
              </div>
              <div className="pp-form-field">
                <label className="pp-info-label">
                  Service <span className="pp-required">*</span>
                </label>
                <select
                  className={`pp-form-input pp-form-select ${
                    payerErrors.service ? "pp-form-input-error" : ""
                  }`}
                  value={payerDrawer.draft.service}
                  onChange={(e) => updatePayerDraft("service", e.target.value)}
                >
                  <option value="">Select service</option>
                  {SERVICE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {payerErrors.service && (
                  <span className="pp-field-error">{payerErrors.service}</span>
                )}
              </div>
              <div className="pp-form-field">
                <label className="pp-info-label">
                  Contracted Rate <span className="pp-required">*</span>
                </label>
                <div className="pp-money-input-wrap">
                  <span className="pp-money-prefix">$</span>
                  <input
                    className={`pp-form-input pp-money-input ${
                      payerErrors.contractedRate ? "pp-form-input-error" : ""
                    }`}
                    value={payerDrawer.draft.contractedRate}
                    onChange={(e) =>
                      updatePayerDraft(
                        "contractedRate",
                        e.target.value.replace(/[^0-9.]/g, ""),
                      )
                    }
                    placeholder="175.00"
                    inputMode="decimal"
                  />
                </div>
                {payerErrors.contractedRate && (
                  <span className="pp-field-error">
                    {payerErrors.contractedRate}
                  </span>
                )}
              </div>
              <div className="pp-form-grid pp-form-grid-2 pp-form-grid-full">
                <div className="pp-form-field">
                  <label className="pp-info-label">
                    Effective Date <span className="pp-required">*</span>
                  </label>
                  <input
                    type="date"
                    className={`pp-form-input ${
                      payerErrors.effectiveDate ? "pp-form-input-error" : ""
                    }`}
                    value={payerDrawer.draft.effectiveDate}
                    onChange={(e) =>
                      updatePayerDraft("effectiveDate", e.target.value)
                    }
                  />
                  {payerErrors.effectiveDate && (
                    <span className="pp-field-error">
                      {payerErrors.effectiveDate}
                    </span>
                  )}
                </div>
                <div className="pp-form-field">
                  <label className="pp-info-label">End Date</label>
                  <input
                    type="date"
                    className="pp-form-input"
                    value={payerDrawer.draft.endDate}
                    onChange={(e) =>
                      updatePayerDraft("endDate", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="pp-license-drawer-footer">
              <button
                type="button"
                className="pp-btn-cancel"
                onClick={closePayerDrawer}
                disabled={savingPayer}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pp-btn-save"
                onClick={submitPayerDrawer}
                disabled={savingPayer}
              >
                {savingPayer
                  ? "Saving..."
                  : payerDrawer.mode === "add"
                    ? "Add Payer"
                    : "Save"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {!readOnlyInsurance && deactivateTarget !== null && (
        <div className="modal-overlay pp-confirm-overlay">
          <div
            className="modal-container pp-confirm-modal pp-deactivate-payer-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pp-deactivate-payer-title"
          >
            <h3 className="pp-confirm-title" id="pp-deactivate-payer-title">
              Deactivate Insurance Payer
            </h3>
            <p className="pp-confirm-body">
              This will prevent this provider from appearing for new sessions
              using this payer, state, and service. Existing scheduled sessions
              will not be cancelled.
            </p>
            <div className="pp-confirm-actions">
              <button
                type="button"
                className="confirm-button pp-confirm-btn-remove"
                onClick={confirmDeactivatePayer}
                disabled={savingPayer}
              >
                {savingPayer ? "Deactivating..." : "Deactivate"}
              </button>
              <button
                type="button"
                className="cancel-button pp-confirm-btn-cancel"
                onClick={closeDeactivateConfirm}
                disabled={savingPayer}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderPaymentsTab;
